import { Box, Text } from '@mantine/core';

interface ProgressStepperProps {
  steps: string[];
  activeStep: number;
  colour: 'blue' | 'green';
}

export function ProgressStepper({ steps, activeStep, colour }: ProgressStepperProps) {
  const accent = colour === 'blue' ? 'var(--cmd-accent)' : 'var(--cmd-success)';
  const accentGlow = colour === 'blue' ? 'var(--cmd-accent-glow)' : 'var(--cmd-success-glow)';
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        width: '100%',
      }}
    >
      {steps.map((label, index) => {
        const isCompleted = index < activeStep;
        const isActive = index === activeStep;
        return (
          <Box
            key={`${label}-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: index < steps.length - 1 ? 1 : undefined,
            }}
          >
            {/* Step node */}
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
              }}
            >
              {/* Circle */}
              <Box
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: isCompleted ? accent : isActive ? accent : 'transparent',
                  border: isCompleted || isActive
                    ? `2px solid ${accent}`
                    : '2px solid var(--border-colour)',
                  boxShadow: isActive
                    ? `0 0 12px ${accentGlow}, 0 2px 8px rgba(0,0,0,0.08)`
                    : isCompleted
                      ? `0 1px 4px rgba(0,0,0,0.06)`
                      : 'none',
                }}
              >
                {/* Pulse ring for active step */}
                {isActive && (
                  <Box
                    style={{
                      position: 'absolute',
                      inset: -6,
                      borderRadius: '50%',
                      border: `2px solid ${accent}`,
                      opacity: 0,
                      zIndex: -1,
                      animation: 'stepperPulseRing 2s ease-out infinite',
                    }}
                  />
                )}
                {isCompleted ? (
                  /* Checkmark */
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    style={{
                      animation: 'stepperCheckDraw 0.3s ease-out forwards',
                    }}
                  >
                    <path
                      d="M3.5 8.5L6.5 11.5L12.5 5"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  /* Step number */
                  <Text
                    fw={600}
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontSize: '0.8125rem',
                      color: isActive ? 'white' : 'var(--text-muted)',
                      lineHeight: 1,
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {index + 1}
                  </Text>
                )}
              </Box>

              {/* Label */}
              <Text
                fw={isActive ? 600 : 500}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.6875rem',
                  color: isCompleted || isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                  letterSpacing: '0.02em',
                  transition: 'color 0.3s ease, font-weight 0.3s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Text>
            </Box>

            {/* Connector line (not after last step) */}
            {index < steps.length - 1 && (
              <Box
                style={{
                  flex: 1,
                  height: 2,
                  marginInline: 8,
                  marginBottom: 20,
                  borderRadius: 1,
                  position: 'relative',
                  backgroundColor: 'var(--border-colour)',
                  overflow: 'hidden',
                }}
              >
                {/* Animated fill */}
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    borderRadius: 1,
                    backgroundColor: accent,
                    width: isCompleted ? '100%' : isActive ? '50%' : '0%',
                    transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: (isCompleted || isActive)
                      ? `0 0 6px ${accentGlow}`
                      : 'none',
                  }}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
